/**
 * Search chips (name / sku / barcode / description / categories) and search history, shared by the storefront and
 * return search bars, both kept per search type.
 *
 * Logged in with a company: saved on the server per user and company.
 * Guests (or no company yet): kept in localStorage, then adopted on login if the user has nothing saved.
 *
 * Host component must provide getData / putData / postData / deleteData (httpMixin, e.g. via productMixin).
 */
const { searchWords } = require("../utils/search-text.js");
const {
    normalizeHistoryTerm,
    normalizeHistory,
    addHistoryTerm,
    removeHistoryTerm,
} = require("../utils/search-history.js");

const SEARCH_FIELD_FALLBACK_LABELS = {
    name: "Name",
    sku: "SKU",
    barcode: "Barcode",
    description: "Description",
    categories: "Categories",
};

const searchPreferenceMixin = {
    props: {
        initialSearchFields: {
            type: Array,
            default: () => ["name", "sku"],
        },
        // the fields this search type allows, in display order (from the service)
        searchFields: {
            type: Array,
            default: () => ["name", "sku", "barcode", "description", "categories"],
        },
        searchPreferenceUrl: {
            type: String,
            default: null,
        },
        canSaveSearchPreference: {
            type: Boolean,
            default: false,
        },
        hasSavedSearchPreference: {
            type: Boolean,
            default: false,
        },
        localSearchPreferenceKey: {
            type: String,
            default: null,
        },
        categorySearchUrl: {
            type: String,
            default: null,
        },
        initialSearchHistory: {
            type: Array,
            default: () => [],
        },
        // POST adds a term, DELETE removes one (or all), PUT {url}/adopt takes over guest history
        searchHistoryUrl: {
            type: String,
            default: null,
        },
        hasSavedSearchHistory: {
            type: Boolean,
            default: false,
        },
        localSearchHistoryKey: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            selectedFields: this.initialSearchSelection(),
            pendingSearchPreference: null,
            searchPreferenceSave: null,
            lastSavedSearchPreference: [...this.initialSearchFields],
            searchHistory: this.initialSearchHistoryList(),
            editingSearchHistory: false,
            // a history chip is opening the results page: no suggestions meanwhile
            leavingForResults: false,
        };
    },

    computed: {
        searchWords() {
            return searchWords(this.searchValue);
        },

        // fields the product search understands; categories are searched separately
        productFields() {
            return this.selectedFields.filter((field) => field !== "categories");
        },

        searchCategories() {
            return this.selectedFields.includes("categories");
        },
    },

    mounted() {
        // search fields used to be kept in localStorage, shared by everyone on the browser
        try {
            localStorage.removeItem("searchOption");
            localStorage.removeItem("returnSearchOption");
        } catch (e) {}

        if (this.canSaveSearchPreference) {
            this.adoptLocalSearchPreference();
            this.adoptLocalSearchHistory();
        }

        // back button restores the page from cache with the flag still set
        globalThis.window?.addEventListener("pageshow", (event) => {
            if (event.persisted) this.leavingForResults = false;
        });
    },

    methods: {
        isSearchFieldSelected(field) {
            return this.selectedFields.includes(field);
        },

        searchFieldLabel(field) {
            return this.headerTranslations?.[field] ?? SEARCH_FIELD_FALLBACK_LABELS[field] ?? field;
        },

        // keeps at least one chip on; returns whether the selection changed
        toggleSearchField(field) {
            const selected = this.isSearchFieldSelected(field);
            if (selected && this.selectedFields.length === 1) {
                return false;
            }

            this.selectedFields = this.searchFields.filter((allowed) =>
                allowed === field ? !selected : this.selectedFields.includes(allowed)
            );
            this.saveSearchPreference(this.selectedFields);

            return true;
        },

        // success(categories, total); an empty list on failure so the section never hangs on its skeleton
        searchCategoryEntries(params, success) {
            if (!this.categorySearchUrl) {
                success([], 0);
                return;
            }

            this.getData(
                this.categorySearchUrl,
                { params },
                (response) => success(response.data?.data ?? [], response.data?.total ?? 0),
                () => success([], 0),
                false
            );
        },

        // Sets the chips on load
        initialSearchSelection() {
            const localFields = this.readLocalSearchPreference();
            const useLocal = !this.canSaveSearchPreference || !this.hasSavedSearchPreference;

            return localFields && useLocal ? localFields : this.initialSearchFields;
        },

        // Guest choice becomes the user's only if they have nothing saved; the server wins otherwise
        adoptLocalSearchPreference() {
            const localFields = this.readLocalSearchPreference();
            if (!localFields) return;

            // cleared by the first instance to mount, so desktop and mobile don't both send it
            this.writeLocalSearchPreference(null);
            if (!this.hasSavedSearchPreference) {
                this.saveSearchPreference(localFields);
            }
        },

        saveSearchPreference(fields) {
            if (!this.canSaveSearchPreference) {
                this.writeLocalSearchPreference(fields);
                return;
            }
            if (!this.searchPreferenceUrl) return;

            this.pendingSearchPreference = [...fields];
            if (!this.searchPreferenceSave) {
                // cleared via .finally so it always runs after this assignment, even when nothing is sent
                this.searchPreferenceSave = this.flushSearchPreference().finally(
                    () => (this.searchPreferenceSave = null)
                );
            }
        },

        // one PUT at a time; clicks made meanwhile collapse into the newest selection
        async flushSearchPreference() {
            while (this.pendingSearchPreference) {
                const fields = this.pendingSearchPreference;
                this.pendingSearchPreference = null;

                if (fields.join() === this.lastSavedSearchPreference.join()) continue;

                await this.putData(
                    this.searchPreferenceUrl,
                    { fields },
                    () => (this.lastSavedSearchPreference = fields),
                    () => {},
                    false
                );
            }
        },

        // leaving the page would cancel an in-flight save
        async waitForSearchPreferenceSave() {
            if (this.searchPreferenceSave) await this.searchPreferenceSave;
        },

        // Allowed fields in canonical order, or null when nothing usable is stored
        readLocalSearchPreference() {
            if (!this.localSearchPreferenceKey) return null;

            try {
                const stored = JSON.parse(localStorage.getItem(this.localSearchPreferenceKey));
                const fields = Array.isArray(stored) ? this.searchFields.filter((field) => stored.includes(field)) : [];

                return fields.length ? fields : null;
            } catch (e) {
                return null;
            }
        },

        // --- search history ---------------------------------------------------------------------------------

        // Like the chips: a guest's browser history is used only when the account has none
        initialSearchHistoryList() {
            const localHistory = this.readLocalSearchHistory();
            const useLocal = !this.canSaveSearchPreference || !this.hasSavedSearchHistory;

            return localHistory.length && useLocal ? localHistory : normalizeHistory(this.initialSearchHistory);
        },

        // Saves a submitted term; await it before leaving the page so the request isn't cancelled
        async recordSearch(term) {
            const cleaned = normalizeHistoryTerm(term);
            if (!cleaned) return;

            this.searchHistory = addHistoryTerm(this.searchHistory, cleaned);

            if (!this.canSaveSearchPreference) {
                this.writeLocalSearchHistory(this.searchHistory);
                return;
            }
            if (!this.searchHistoryUrl) return;

            await this.postData(this.searchHistoryUrl, { term: cleaned }, () => {}, () => {}, false);
        },

        removeSearchHistoryTerm(term) {
            this.searchHistory = removeHistoryTerm(this.searchHistory, term);
            if (!this.searchHistory.length) {
                this.editingSearchHistory = false;
            }

            if (!this.canSaveSearchPreference) {
                this.writeLocalSearchHistory(this.searchHistory);
            } else if (this.searchHistoryUrl) {
                this.deleteData(this.searchHistoryUrl, { params: { term } }, () => {}, () => {}, false);
            }
        },

        clearSearchHistory() {
            this.searchHistory = [];
            this.editingSearchHistory = false;

            if (!this.canSaveSearchPreference) {
                this.writeLocalSearchHistory(null);
            } else if (this.searchHistoryUrl) {
                this.deleteData(this.searchHistoryUrl, {}, () => {}, () => {}, false);
            }
        },

        // cleared by the first instance to mount, so desktop and mobile don't both send it
        adoptLocalSearchHistory() {
            const localHistory = this.readLocalSearchHistory();
            if (!localHistory.length) return;

            this.writeLocalSearchHistory(null);
            if (!this.hasSavedSearchHistory && this.searchHistoryUrl) {
                this.putData(`${this.searchHistoryUrl}/adopt`, { terms: localHistory }, () => {}, () => {}, false);
            }
        },

        readLocalSearchHistory() {
            if (!this.localSearchHistoryKey) return [];

            try {
                return normalizeHistory(JSON.parse(localStorage.getItem(this.localSearchHistoryKey)));
            } catch (e) {
                return [];
            }
        },

        writeLocalSearchHistory(terms) {
            if (!this.localSearchHistoryKey) return;

            try {
                if (terms && terms.length) {
                    localStorage.setItem(this.localSearchHistoryKey, JSON.stringify(terms));
                } else {
                    localStorage.removeItem(this.localSearchHistoryKey);
                }
            } catch (e) {}
        },

        writeLocalSearchPreference(fields) {
            if (!this.localSearchPreferenceKey) return;

            try {
                if (fields) {
                    localStorage.setItem(this.localSearchPreferenceKey, JSON.stringify(fields));
                } else {
                    localStorage.removeItem(this.localSearchPreferenceKey);
                }
            } catch (e) {}
        },
    },
};

module.exports = { searchPreferenceMixin };
